import React from 'react';
import { Line } from 'react-chartjs-2';
import { useTheme } from '../../contexts/ThemeContext';
import { getChartJsOptions, getChartColors, getChartBackgroundColor } from '../../utils/themeHelpers';
import { getResponsiveXAxisTicks } from '../../utils/chartResponsive';
import { TYPOGRAPHY_CONFIG } from '../../config/typography.config';

interface SalesTrendData {
  month: string;
  sales: number;
  invoices: number;
}

interface NormalizedChartProps {
  data: SalesTrendData[];
  currencySymbol: string;
  formatKpiValue: (value: number | string, type?: 'currency' | 'percentage' | 'number') => string;
}

const NormalizedChart: React.FC<NormalizedChartProps> = ({
  data,
  currencySymbol,
  formatKpiValue,
}) => {
  const { currentTheme } = useTheme();

  // Calculate normalized values (0-100 scale)
  const salesValues = data.map(item => item.sales || 0);
  const invoiceValues = data.map(item => item.invoices || 0);
  
  const maxSales = Math.max(...salesValues);
  const minSales = Math.min(...salesValues);
  const maxInvoices = Math.max(...invoiceValues);
  const minInvoices = Math.min(...invoiceValues);
  
  const normalizedSales = salesValues.map(val => 
    maxSales !== minSales ? ((val - minSales) / (maxSales - minSales)) * 100 : 50
  );
  const normalizedInvoices = invoiceValues.map(val => 
    maxInvoices !== minInvoices ? ((val - minInvoices) / (maxInvoices - minInvoices)) * 100 : 50
  );

  return (
    <>
      <h3 className="title-card">مقایسه هم‌زمان فروش و تعداد سفارش</h3>
      <div className="mobile-chart-host" style={{ height: 400, width: '100%' }} role="img" aria-label="نمودار مقایسه نرمال‌شده روند فروش">
        <Line
          data={{
            labels: data.map(item => item.month || ''),
            datasets: [
              {
                label: 'مبلغ فروش (نرمال‌شده)',
                data: normalizedSales,
                borderColor: getChartColors(currentTheme)[0],
                backgroundColor: getChartBackgroundColor(0, currentTheme, 0.1),
                borderWidth: 3,
                pointRadius: 5,
                pointHoverRadius: 7,
                pointBackgroundColor: getChartColors(currentTheme)[0],
                tension: 0.3,
                fill: false,
              },
              {
                label: 'تعداد سفارش (نرمال‌شده)',
                data: normalizedInvoices,
                borderColor: getChartColors(currentTheme)[1],
                backgroundColor: getChartBackgroundColor(1, currentTheme, 0.1),
                borderWidth: 3,
                pointRadius: 5,
                pointHoverRadius: 7,
                pointBackgroundColor: getChartColors(currentTheme)[1],
                tension: 0.3,
                fill: false,
              }
            ]
          }}
          options={{
            ...getChartJsOptions(currentTheme),
            interaction: {
              mode: 'index' as const,
              intersect: false,
            },
            plugins: {
              ...getChartJsOptions(currentTheme).plugins,
              legend: {
                ...getChartJsOptions(currentTheme).plugins?.legend,
                position: 'top' as const,
              },
              tooltip: {
                ...getChartJsOptions(currentTheme).plugins?.tooltip,
                callbacks: {
                  ...(getChartJsOptions(currentTheme).plugins?.tooltip?.callbacks || {}),
                  label: function(context: any) {
                    const datasetIndex = context.datasetIndex;
                    const index = context.dataIndex;
                    const label = context.dataset.label || '';
                    const normalizedValue = context.parsed.y;
                    
                    const separator = '\u00A0:\u00A0';
                    if (datasetIndex === 0) {
                      const actualValue = salesValues[index];
                      return [
                        `${label}${separator}${normalizedValue.toFixed(1)}%`,
                        `مقدار واقعی${separator}${formatKpiValue(actualValue, 'currency')} ${currencySymbol}`
                      ];
                    } else {
                      const actualValue = invoiceValues[index];
                      return [
                        `${label}${separator}${normalizedValue.toFixed(1)}%`,
                        `مقدار واقعی${separator}${formatKpiValue(actualValue)} سفارش`
                      ];
                    }
                  }
                }
              }
            },
            scales: {
              x: {
                ...getChartJsOptions(currentTheme).scales?.x,
                title: {
                  display: true,
                  text: 'ماه',
                  color: 'var(--chart-axis-color)',
                  font: {
                    family: 'var(--font-primary)',
                    size: TYPOGRAPHY_CONFIG.components.chart.axisLabel.size,
                    weight: 600,
                  },
                },
                ticks: getResponsiveXAxisTicks(
                  getChartJsOptions(currentTheme).scales?.x?.ticks as Record<string, unknown> | undefined
                ),
              },
              y: {
                ...getChartJsOptions(currentTheme).scales?.y,
                beginAtZero: true,
                min: 0,
                max: 110,
                ticks: {
                  ...getChartJsOptions(currentTheme).scales?.y?.ticks,
                  callback: function(value: any) {
                    return value <= 100 ? `${value}%` : '';
                  },
                  stepSize: 20,
                },
                title: {
                  display: true,
                  text: 'درصد از حداکثر',
                  color: 'var(--chart-axis-color)',
                  font: {
                    family: 'var(--font-primary)',
                    size: TYPOGRAPHY_CONFIG.components.chart.axisLabel.size,
                    weight: 600,
                  },
                },
              },
            }
          }}
        />
      </div>
    </>
  );
};

export default NormalizedChart;